/**
 * An in-house payment plan: a customer who pays the company itself, month by
 * month, instead of a lender.
 *
 * Jonathon, 2026-10-14: "we need an in house financing process. for example.
 * durward paid $500 up front and then will mail us a check for $100 a month
 * until he pays off his $2500 total."
 *
 * The plan sits on top of the payment schedule rather than beside it. The
 * down payment is the schedule's First payment, so signing asks for it the way
 * it asks for any deposit. What the plan pays off is the Final — whatever the
 * First and any draws leave behind (ADR 0031). There is no plan fee and no
 * interest (Jonathon, 2026-10-15: "let's not do a fee anymore"). So Durward's $2,500 roof with $500 down is $2,000 on the plan:
 * twenty payments of $100.
 *
 * Nothing here knows about checks, Mercury or the to-do list. It answers two
 * questions from numbers: what is due when (`planInstalments`), and how a
 * customer stands against that today (`planStanding`).
 */

import type { Balance } from "./balance.js";
import { FINAL_SLOT } from "./paymentSchedule.js";
import { cents, formatMoney, type Cents } from "./money.js";
import type { PaymentMatch } from "./paymentReceipt.js";
import { longDate } from "./stormReportHtml.js";

/** What a rep sets up on the estimate. */
export interface PlanTerms {
  /** What the customer pays each month. */
  monthlyCents: Cents;
  /** The first monthly payment's due date, `YYYY-MM-DD`. Later ones fall on the same day of the month. */
  firstDueOn: string;
}

/**
 * The plan an estimate row carries, or null when it has none.
 *
 * Read off the two `plan_*` columns, so the builder, the proposal and the
 * job screen all ask the same question the same way.
 */
export function planTermsOf(
  estimate:
    | {
        plan_monthly_cents?: number | null;
        plan_first_due_on?: string | null;
      }
    | null
    | undefined,
): PlanTerms | null {
  const monthly = Number(estimate?.plan_monthly_cents ?? 0);
  const first = String(estimate?.plan_first_due_on ?? "").slice(0, 10);
  if (!(monthly > 0) || !parseDay(first)) return null;
  return {
    monthlyCents: cents(Math.round(monthly)),
    firstDueOn: first,
  };
}

/** One monthly payment. */
export interface PlanInstalment {
  /** 1 for the first monthly payment. */
  number: number;
  dueOn: string;
  amount: Cents;
}

/**
 * How many days after its due date a monthly check counts as late.
 *
 * Checks come by mail, so a check due on the 1st that arrives on the 4th is
 * on time as far as anybody cares. Built: five days. Jonathon's call on
 * 2026-10-14 was only that the office is told, not the customer.
 */
export const PLAN_LATE_AFTER_DAYS = 5;

/** The most monthly payments a plan can have: twenty years. A guard against a $1-a-month typo, not a policy. */
export const PLAN_MAX_INSTALMENTS = 240;

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDay(value: string): { y: number; m: number; d: number } | null {
  const match = DATE.exec(value.trim());
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

const pad = (n: number) => String(n).padStart(2, "0");

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * The same day of the month, `months` later.
 *
 * A plan due on the 31st is due on the 30th in a 30-day month and on the 28th
 * or 29th in February, and back on the 31st after — the day the customer
 * agreed to, as near as the calendar allows.
 */
export function addMonths(day: string, months: number): string {
  const parsed = parseDay(day);
  if (!parsed) return day;
  const index = parsed.y * 12 + (parsed.m - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const d = Math.min(parsed.d, daysInMonth(y, m));
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** `day` plus `days` calendar days. */
function addDays(day: string, days: number): string {
  const parsed = parseDay(day);
  if (!parsed) return day;
  const at = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d + days));
  return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`;
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
function daysBetween(from: string, to: string): number {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b) return 0;
  return Math.round(
    (Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000,
  );
}

/**
 * What is wrong with these terms, in words a rep reads, or null.
 *
 * `financed` is what the plan has to pay off: the Final.
 */
export function planProblem(financed: Cents, terms: PlanTerms): string | null {
  const monthly = Math.round(Number(terms.monthlyCents));
  if (!(monthly > 0)) return "Type what the customer pays each month.";
  if (!parseDay(terms.firstDueOn))
    return "Pick the date the first monthly payment is due.";
  if (financed <= 0)
    return "There is nothing left to pay monthly after the down payment.";
  if (Math.ceil(financed / monthly) > PLAN_MAX_INSTALMENTS) {
    return `At ${formatMoney(cents(monthly))} a month this would take more than ${PLAN_MAX_INSTALMENTS / 12} years.`;
  }
  return null;
}

/**
 * Every monthly payment, in order.
 *
 * Equal payments of the monthly amount, and a last one for whatever is left,
 * which is smaller rather than larger: $2,050 at $100 a month is twenty
 * payments of $100 and a twenty-first of $50. Empty when the terms are not
 * usable (`planProblem`).
 */
export function planInstalments(
  financed: Cents,
  terms: PlanTerms,
): PlanInstalment[] {
  if (planProblem(financed, terms) !== null) return [];
  return spread(financed, terms);
}

/**
 * The most monthly payments a job's plan is ever worked out to. Past
 * `PLAN_MAX_INSTALMENTS` only when what was bought together runs over it;
 * this only keeps a cent-a-month typo from building a million rows.
 */
const SPREAD_LIMIT = PLAN_MAX_INSTALMENTS * 5;

/** The monthly payments, with no cap but `SPREAD_LIMIT`. */
function spread(financed: Cents, terms: PlanTerms): PlanInstalment[] {
  const monthly = Math.round(Number(terms.monthlyCents));
  if (!(monthly > 0) || !parseDay(terms.firstDueOn)) return [];
  if (Math.ceil(Number(financed) / monthly) > SPREAD_LIMIT) return [];
  let left = Math.round(Number(financed));
  const out: PlanInstalment[] = [];
  while (left > 0) {
    const amount = Math.min(monthly, left);
    out.push({
      number: out.length + 1,
      dueOn: addMonths(terms.firstDueOn, out.length),
      amount: cents(amount),
    });
    left -= amount;
  }
  return out;
}

/** How a customer stands against their plan on a given day. */
export interface PlanStanding {
  /** What the plan pays off: the Final. */
  total: Cents;
  /** What the customer has paid toward it. */
  paid: Cents;
  /** What is left on the plan. */
  remaining: Cents;
  paidOff: boolean;
  /** Monthly payments made in full. */
  paymentsMade: number;
  paymentsTotal: number;
  /** The next payment the customer owes, and how much of it is still to pay. Null once paid off. */
  next: { dueOn: string; amount: Cents } | null;
  /** What should have arrived by today and has not. */
  behind: Cents;
  /** The due date of the oldest payment not yet made, when it has passed. */
  overdueSince: string | null;
  /** Whether that is more than `PLAN_LATE_AFTER_DAYS` ago — the office is told. */
  late: boolean;
  daysLate: number;
}

/**
 * How a customer stands today.
 *
 * Money is applied oldest payment first, whatever the checks were for: a
 * customer who sends $200 one month and nothing the next is up to date for
 * both. So "behind" is simply what was due by today less what has been paid.
 */
export function planStanding(
  instalments: readonly PlanInstalment[],
  paidCents: Cents,
  today: string,
): PlanStanding {
  const total = cents(instalments.reduce((sum, row) => sum + row.amount, 0));
  const paid = cents(Math.max(0, Math.round(Number(paidCents))));
  const remaining = cents(Math.max(0, total - paid));

  let covered = paid as number;
  let paymentsMade = 0;
  let next: PlanStanding["next"] = null;
  for (const row of instalments) {
    if (covered >= row.amount) {
      covered -= row.amount;
      paymentsMade += 1;
      continue;
    }
    next = { dueOn: row.dueOn, amount: cents(row.amount - covered) };
    break;
  }

  const dueByToday = instalments
    .filter((row) => row.dueOn <= today)
    .reduce((sum, row) => sum + row.amount, 0);
  const behind = cents(Math.max(0, dueByToday - paid));
  const overdueSince = behind > 0 && next ? next.dueOn : null;
  const daysLate = overdueSince
    ? Math.max(0, daysBetween(overdueSince, today))
    : 0;

  return {
    total,
    paid,
    remaining,
    paidOff: total > 0 && remaining === 0,
    paymentsMade,
    paymentsTotal: instalments.length,
    next,
    behind,
    overdueSince,
    late:
      overdueSince !== null &&
      today >= addDays(overdueSince, PLAN_LATE_AFTER_DAYS),
    daysLate,
  };
}

/** A plan with nothing left on it, on a job with nothing left owing. */
const PAID_OFF_WITH_NOTHING_ON_IT: PlanStanding = {
  total: cents(0),
  paid: cents(0),
  remaining: cents(0),
  paidOff: true,
  paymentsMade: 0,
  paymentsTotal: 0,
  next: null,
  behind: cents(0),
  overdueSince: null,
  late: false,
  daysLate: 0,
};

/**
 * How a job's plan stands, from the job's balance.
 *
 * The plan pays off the Final, and `jobBalance` already knows what the Final
 * is — the contract less the down payment and any draws — so the job screen
 * and the to-do list both start from that rather than working it out again.
 * `sentCents` is every check or cash payment recorded toward the plan:
 * banked or still clearing for the Balance card and the to-do list, since
 * whether a customer is late is about whether they sent the check; banked
 * only for Final Check.
 *
 * A monthly payment by bank transfer, Zelle or card is never a recorded
 * check. It is a confirmed credit that settled no scheduled payment, which
 * the balance already counts as `offSchedule`, so it counts here too. And a
 * balance with nothing left owing has paid the plan off, however the money
 * came.
 */
export function jobPlanStanding(
  balance: Balance,
  terms: PlanTerms,
  sentCents: Cents,
  today: string,
): PlanStanding | null {
  const final =
    balance.lines.find((line) => line.slot === FINAL_SLOT)?.amount ?? cents(0);
  // Not `planInstalments`: a plan the customer signed runs even when what
  // they bought together is past the builder's twenty-year guard, or the job
  // could never take its Final Check (review of #933).
  const rows = spread(final, terms);
  const settled = balance.contract > 0 && balance.outstanding === 0;
  if (rows.length === 0) return settled ? PAID_OFF_WITH_NOTHING_ON_IT : null;
  const sent =
    Math.max(0, Math.round(Number(sentCents) || 0)) +
    Math.max(0, Math.round(Number(balance.offSchedule ?? 0)));
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  return planStanding(
    rows,
    cents(settled ? Math.max(sent, total) : sent),
    today,
  );
}

/**
 * Everything recorded toward a plan, added up: what the customer has sent.
 *
 * The job screen's card, the to-do list and the balance all start from this,
 * so they cannot disagree about whether a customer is late. A payment never
 * counts for less than nothing.
 */
export function planSentCents(
  payments: readonly { amountCents: number }[],
): Cents {
  return cents(
    payments.reduce(
      (sum, p) => sum + Math.max(0, Math.round(Number(p.amountCents) || 0)),
      0,
    ),
  );
}

/**
 * Whether a payment may be recorded as this scheduled payment.
 *
 * On a plan the Final is paid month by month, never as one payment: a $100
 * check recorded as the Final would settle all of it. Every path that records
 * money against a slot — a person's form, a deposit confirmed on Money In, a
 * box ticked, an agent's tick — asks this (CLAUDE.md rule 5).
 */
export function mayPaySlot(slot: number, onPlan: boolean): boolean {
  return !(onPlan && Number(slot) === FINAL_SLOT);
}

/** Why a payment cannot be the Final on a plan job, in words a rep reads. */
export const PLAN_FINAL_REFUSED =
  "This job is on a payment plan, so the Final is paid month by month and " +
  "never as one payment. Record the check toward the payment plan instead.";

/**
 * Whether a cleared box ("First Check", "Final Check") may be ticked.
 *
 * On a plan, Final Check says the plan is paid off: the monthly checks are
 * what pay the Final, and each is marked in the bank on its own. So it may
 * be ticked once the banked plan payments cover the plan, and not before,
 * and the refusal comes before the box is written. Off a plan, and for every
 * other box, nothing here stands in the way. The web app and Creed both ask
 * this (CLAUDE.md rule 5).
 */
export function mayTickClearedBox(
  slot: number,
  plan: { onPlan: boolean; paidOff: boolean },
): boolean {
  return mayPaySlot(slot, plan.onPlan && !plan.paidOff);
}

/** Why Final Check cannot be ticked yet on a plan job, in words a rep reads. */
export const PLAN_FINAL_CHECK_REFUSED =
  "This job is on a payment plan, so Final Check is ticked once the plan is " +
  "paid off. Until then, record each check toward the payment plan and mark " +
  "it in the bank on the Balance card when it lands. The box was not ticked.";

/**
 * Whether a recorded payment may be marked in the bank by hand.
 *
 * A plan payment names no slot, so no box ever settles it, and a check
 * Mercury never matches would otherwise stay on its way for good. Only one
 * still waiting: a payment already in the bank stays there.
 */
export function mayMarkPlanPaymentBanked(payment: {
  toward_plan?: boolean | null;
  state: string;
}): boolean {
  return payment.toward_plan === true && payment.state === "submitted";
}

/**
 * What an amount matched by arithmetic settles on a plan job.
 *
 * Off a plan, the match stands. On one, a match that is the Final (or takes
 * the Final with it), and an amount that matches nothing, are a monthly
 * payment toward the plan, counted at what was handed over. A down payment or
 * a draw that matches stays what it is.
 */
export function settledOnPlan(
  match: PaymentMatch,
  onPlan: boolean,
): { match: PaymentMatch; towardPlan: boolean } {
  if (!onPlan) return { match, towardPlan: false };
  if (match.matched && match.slots.every((slot) => mayPaySlot(slot, true))) {
    return { match, towardPlan: false };
  }
  return {
    match: {
      matched: false,
      slots: [],
      why: "Recorded toward the payment plan.",
    },
    towardPlan: true,
  };
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/**
 * The terms alone, for a plan whose count cannot be worked out.
 *
 * "$100.00 a month, due on the 1st of each month from November 1, 2026."
 * What a proposal prints when the bought set's Final is one `planProblem`
 * refuses, such as two packages that each fit and together run past twenty
 * years: still a payment plan, never a lump-sum Final (review of #933).
 */
export function planTermsWording(terms: PlanTerms): string | null {
  const parsed = parseDay(terms.firstDueOn);
  const monthly = Math.round(Number(terms.monthlyCents));
  if (!parsed || !(monthly > 0)) return null;
  return (
    `${formatMoney(cents(monthly))} a month, due on the ` +
    `${ordinal(parsed.d)} of each month from ${longDate(terms.firstDueOn)}.`
  );
}

/**
 * The sentence the customer reads on their proposal, beside the Final.
 *
 * "$100.00 a month, due on the 1st of each month from November 1, 2026,
 * 20 payments, the last due June 1, 2028." With a smaller last payment: "20 payments of $100.00 and
 * a last payment of $50.00, due July 22, 2028".
 */
export function planWording(financed: Cents, terms: PlanTerms): string | null {
  const rows = planInstalments(financed, terms);
  const parsed = parseDay(terms.firstDueOn);
  const last = rows.at(-1);
  if (!last || !parsed) return null;
  const monthly = cents(Math.round(Number(terms.monthlyCents)));
  // Jonathon, 2026-10-15: say when the last payment is due.
  const lastDue = longDate(last.dueOn);
  const count =
    rows.length === 1
      ? "1 payment"
      : last.amount === monthly
        ? `${rows.length} payments, the last due ${lastDue}`
        : `${rows.length - 1} payment${rows.length - 1 === 1 ? "" : "s"} of ${formatMoney(monthly)} and a last payment of ${formatMoney(last.amount)}, due ${lastDue}`;
  return (
    `${formatMoney(monthly)} a month, due on the ` +
    `${ordinal(parsed.d)} of each month from ${longDate(terms.firstDueOn)}, ` +
    `${count}.`
  );
}
